import * as React from 'react'
import { Check, ChevronRight } from 'lucide-react'
import type { IrrigationProblemDto } from '@lib/dto/irrigation.dto'
import type { IrrigationProblemTypeDto } from '@lib/dto/irrigation-problem-type.dto'
import { PROBLEM_SEVERITY_LABELS } from '@lib/enums/problem-severity.enum'
import type { CommitResult } from '@/hooks/useIrrigationSurvey'
import { Button } from '@/components/ui/button'
import { problemTitle } from '@/irrigation-survey/irrigationLabels'
import { formatVisitDay } from '@/site-visits/VisitBar'
import { isBroughtForward } from '@/site-visits/lastVisit'
import { cn } from '@/lib/utils'

/**
 * The visit before this one, as the irrigation sheets show it.
 *
 * Loading and missing have no problems yet. Ready does, even when that list
 * is empty: nothing was recorded, which is different from not knowing.
 */
export type IrrigationLastVisit =
  | { status: 'loading'; startedOn: string }
  | { status: 'missing'; startedOn: string }
  | {
      status: 'ready'
      startedOn: string
      problems: readonly IrrigationProblemDto[]
    }

/** "Fixed Oct 20", from the instant the repair's job was done. */
function fixedOn(fixedAt: string): string {
  return `Fixed ${new Date(fixedAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })}`
}

/**
 * "Moderate · Mower strike · Fixed Oct 20". An "Other" problem's notes are
 * its title already.
 */
function detail(problem: IrrigationProblemDto): string {
  return [
    problem.severity === null
      ? 'Not rated'
      : PROBLEM_SEVERITY_LABELS[problem.severity],
    problem.typeId === null ? null : problem.notes,
    problem.fixedAt === null ? null : fixedOn(problem.fixedAt),
  ]
    .filter((part): part is string => part !== null)
    .join(' · ')
}

/**
 * "Last visit" in a sheet's problems on a revisit: what the visit before this
 * one found on the same thing, read-only, with Still here to bring a problem
 * into this visit (docs/site-visits.md). A fixed one can be brought too: a
 * repair that did not hold is the problem coming back.
 */
export function LastVisitProblems({
  lastVisit,
  problems,
  current,
  problemTypes,
  initiallyOpen = false,
  onBring,
}: {
  lastVisit: IrrigationLastVisit
  /** Last visit's problems on this thing. */
  problems: readonly IrrigationProblemDto[]
  /** This visit's problems on the site, to tell which were brought already. */
  current: readonly IrrigationProblemDto[]
  problemTypes: readonly IrrigationProblemTypeDto[]
  /** Open, and scrolled into view, as when a faint pin on the map is tapped. */
  initiallyOpen?: boolean
  onBring: (problem: IrrigationProblemDto) => CommitResult
}) {
  const [open, setOpen] = React.useState(initiallyOpen)
  const [notice, setNotice] = React.useState<string | null>(null)
  const sectionRef = React.useRef<HTMLDivElement | null>(null)

  // The zone's problems are at the foot of its sheet, under its heads.
  React.useEffect(() => {
    if (initiallyOpen) sectionRef.current?.scrollIntoView({ block: 'nearest' })
  }, [initiallyOpen])

  function summary(): string {
    if (lastVisit.status !== 'ready') return ''
    if (problems.length === 0) return 'Nothing recorded'
    return `${String(problems.length)} ${problems.length === 1 ? 'problem' : 'problems'}`
  }

  function renderBody(): React.ReactNode {
    if (lastVisit.status === 'loading') {
      return <p className="text-xs text-muted-text">Loading last visit…</p>
    }
    if (lastVisit.status === 'missing') {
      return (
        <p className="text-xs text-muted-text">
          Last visit is not on this phone. Open the site with a signal to
          bring it.
        </p>
      )
    }
    if (problems.length === 0) {
      return (
        <p className="text-xs text-muted-text">No problems were recorded.</p>
      )
    }
    return (
      <ul className="divide-y divide-default rounded-md border border-default bg-surface">
        {problems.map((problem) => (
          <li
            key={problem.id}
            className="flex items-center justify-between gap-2 px-3 py-2"
          >
            <span className="min-w-0">
              <span className="block truncate text-sm">
                {problemTitle(problem, problemTypes)}
              </span>
              <span className="block truncate text-xs text-muted-text">
                {detail(problem)}
              </span>
            </span>
            {isBroughtForward(problem, current) ? (
              <span className="flex shrink-0 items-center gap-1 text-xs text-secondary-text">
                <Check className="h-3.5 w-3.5 text-success" />
                Still here
              </span>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-11 shrink-0 lg:h-8"
                onClick={() => {
                  const result = onBring(problem)
                  setNotice(result.ok ? null : result.message)
                }}
              >
                Still here
              </Button>
            )}
          </li>
        ))}
      </ul>
    )
  }

  return (
    <div
      ref={sectionRef}
      className="rounded-md border border-default bg-subtle"
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current)
        }}
        className="flex h-11 w-full items-center gap-1.5 px-3 text-left text-sm lg:h-9"
      >
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-muted-text transition-transform',
            open ? 'rotate-90' : null,
          )}
        />
        <span className="font-medium">
          Last visit · {formatVisitDay(lastVisit.startedOn)}
        </span>
        <span className="ml-auto truncate text-xs text-muted-text">
          {summary()}
        </span>
      </button>
      {open ? (
        <div className="space-y-1.5 px-3 pb-3">
          {renderBody()}
          {notice === null ? null : (
            <p role="alert" className="text-xs text-danger">
              {notice}
            </p>
          )}
        </div>
      ) : null}
    </div>
  )
}
